import React, { useRef } from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { useMyContext } from "../../MyContext";
import { Link } from "react-router-dom";
import polopipesImage from "../../image/PoloPipes.png";

function Carousel() {
  const contextData = useMyContext();
  const tempObj = contextData.productObj || [];
  const sliderRef = useRef(null);

  const productCount = tempObj.length;

  const sliderSettings = {
    arrows: false,
    dots: false,
    slidesToShow: Math.min(4, productCount || 1),
    slidesToScroll: 1,
    infinite: productCount > 1, // Must be true for autoplay to loop
    autoplay: productCount > 1,  // Enable autoplay only when items exist
    autoplaySpeed: 2000,
    speed: 600,
    pauseOnHover: true,
    pauseOnFocus: false,
    swipe: true,
    touchMove: true,
    responsive: [
      {
        breakpoint: 1280,
        settings: {
          slidesToShow: Math.min(3, productCount || 1),
        },
      },
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: Math.min(2, productCount || 1),
        },
      },
      {
        breakpoint: 640,
        settings: {
          slidesToShow: 1,
        },
      },
    ],
  };

  const handleProduct = (path) => {
    if (typeof window !== "undefined") {
      window.location.href = path;
    }
  };

  return (
    <section className="w-full bg-slate-50/50 py-16 px-6 sm:px-12 relative overflow-hidden">
      <div className="max-w-7xl mx-auto flex flex-col items-center justify-center relative z-10">

        {/* Header Tag & Title */}
        <div className="flex flex-col mb-12 text-center space-y-2">
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold tracking-wider uppercase bg-orange-100 text-orange-700 mx-auto">
            Our Portfolio
          </span>
          <h2 className="text-3xl sm:text-4xl font-black text-slate-900 tracking-tight">
            Featured Products
          </h2>
          <p className="text-slate-600 text-xs sm:text-sm max-w-md mx-auto">
            Explore our range of BIS-certified piping solutions and water management assets.
          </p>
        </div>

        {/* Carousel Container */}
        <div className="w-full relative">
          {productCount > 0 ? (
            <>
              {/* Note key={productCount} here: forces slider reset when context populates */}
              <Slider key={productCount} className="border-none" ref={sliderRef} {...sliderSettings}>
                {tempObj.map((card, index) => {
                  let productImage;
                  try {
                    productImage = require(`../../image/products/${card?.image}`);
                  } catch (e) {
                    productImage = polopipesImage;
                  }

                  return (
                    <div key={index} className="px-3 py-4 focus:outline-none">
                      <div className="group mx-auto max-w-xs sm:max-w-sm w-full bg-white border border-slate-200/80 flex flex-col relative shadow-lg shadow-slate-200/50 hover:shadow-2xl hover:shadow-orange-500/10 rounded-2xl transition-all duration-300 overflow-hidden hover:-translate-y-1">

                        {/* Image Container with Badge */}
                        <div className="bg-slate-100 relative aspect-square overflow-hidden flex-shrink-0">
                          <span className="absolute top-3 left-3 z-10 bg-slate-900/80 backdrop-blur-md text-white text-[10px] font-bold px-2.5 py-1 rounded-full tracking-wider uppercase border border-white/10">
                            BIS Certified
                          </span>
                          <img
                            src={productImage}
                            alt={card?.title || "Product Display"}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ease-out"
                          />
                        </div>

                        {/* Product Info Block */}
                        <div className="p-5 bg-white flex items-center justify-between flex-col flex-1 space-y-3">
                          <div className="text-center w-full space-y-1">
                            <h3 className="text-base font-bold text-slate-900 truncate tracking-tight group-hover:text-orange-600 transition-colors">
                              {card?.title}
                            </h3>
                            <p className="text-slate-500 text-xs font-medium line-clamp-2 max-w-[220px] mx-auto leading-relaxed">
                              {card?.description}
                            </p>
                          </div>

                          <Link to={`/product/${card?.id}`} className="w-full pt-1">
                            <button className="w-full flex items-center justify-center bg-slate-900 hover:bg-orange-600 text-white rounded-xl h-10 text-xs font-bold tracking-wide transition-all duration-300 cursor-pointer shadow-md">
                              View Details
                            </button>
                          </Link>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </Slider>

              {/* Desktop Navigation Arrow Left */}
              <div className="hidden lg:block">
                <div className="absolute -left-5 xl:-left-7 inset-y-0 flex items-center z-20">
                  <button
                    className="p-3 rounded-full border border-slate-200 bg-white/90 backdrop-blur-md text-slate-700 hover:text-orange-600 hover:border-orange-500 shadow-xl cursor-pointer transition-all duration-300 hover:scale-110"
                    onClick={() => sliderRef.current?.slickPrev()}
                    aria-label="Previous slide"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2.5" stroke="currentColor" className="w-4 h-4">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Desktop Navigation Arrow Right */}
              <div className="hidden lg:block">
                <div className="absolute -right-5 xl:-right-7 inset-y-0 flex items-center z-20">
                  <button
                    className="p-3 rounded-full border border-slate-200 bg-white/90 backdrop-blur-md text-slate-700 hover:text-orange-600 hover:border-orange-500 shadow-xl cursor-pointer transition-all duration-300 hover:scale-110"
                    onClick={() => sliderRef.current?.slickNext()}
                    aria-label="Next slide"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2.5" stroke="currentColor" className="w-4 h-4">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                    </svg>
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="w-full h-64 flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
            </div>
          )}
        </div>

        {/* Footer Action Bar */}
        <div className="flex flex-col sm:flex-row items-center mt-10 pt-6 space-y-3 sm:space-y-0 sm:space-x-4 justify-center border-t border-slate-200/80 w-full">
          <span className="text-slate-600 text-sm font-medium">
            Looking for complete product specifications?
          </span>
          <button
            onClick={() => handleProduct("/products")}
            className="bg-gradient-to-r from-orange-500 via-amber-500 to-orange-600 hover:opacity-95 font-bold text-white text-xs sm:text-sm h-10 px-6 rounded-xl shadow-md shadow-orange-500/20 transition-all tracking-wide cursor-pointer"
          >
            Explore All Products
          </button>
        </div>

      </div>
    </section>
  );
}

export default Carousel;